import { AlertTriangle, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Diálogo compartido para mostrar impedimentos por integrante (feb-2026).
 *
 * Backend contract (respuesta HTTP 400 desde `contratar_grupal`, `contratar`,
 * `comite_decision` y cualquier endpoint que valide datos fiscales + KYC + SIC):
 * ```
 * detail: {
 *   mensaje: string,
 *   integrantes_bloqueados: Array<{
 *     cliente_id: string,
 *     cliente: string,
 *     rol: "titular" | "obligado_solidario",
 *     problemas: string[]
 *   }>
 * }
 * ```
 */
export function BloqueoIntegrantesDialog({ data, onClose }) {
  return (
    <Dialog open={!!data} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="bloqueo-integrantes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            No se puede continuar
          </DialogTitle>
          <DialogDescription>
            {data?.mensaje} Corrige cada punto y reintenta.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-3">
          {(data?.integrantes || []).map((b) => (
            <div
              key={`bloq-${b.cliente_id}`}
              data-testid={`bloqueo-integrante-${b.cliente_id}`}
              className="rounded-lg border border-rose-200 bg-rose-50/50 p-3"
            >
              <div className="mb-2 flex items-center gap-2">
                <UserCog className="h-4 w-4 text-rose-700" />
                <span className="font-semibold text-rose-900">{b.cliente}</span>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    b.rol === "titular"
                      ? "bg-rose-700 text-white"
                      : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {b.rol === "titular" ? "Titular" : "Obligado solidario"}
                </span>
              </div>
              <ul className="list-inside list-disc space-y-1 text-sm text-rose-800">
                {(b.problemas || []).map((p, i) => (
                  <li
                    key={`p-${b.cliente_id}-${i}`}
                    data-testid={`bloqueo-problema-${b.cliente_id}-${i}`}
                  >
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            onClick={() => onClose?.()}
            data-testid="bloqueo-cerrar-btn"
            variant="outline"
          >
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
